/* ============================================================
   Mobile Navigation — State Machine (LOCKED)
   AUTHORITY: assets/mobile-menu.js

   RESPONSIBILITIES
   - Mount / unmount inert mobile nav payload INTO drawer mount point
   - Handle forward / back navigation
   - Enforce single active panel
   - Reset state on close

   NON-NEGOTIABLE
   - No Shopify knowledge
   - No layout manipulation
   - No styling
   - No desktop coupling
============================================================ */

(function () {
  const TEMPLATE_ID = 'mobile-nav-payload';
  const OPEN_ATTR = 'data-mobile-nav-open';
  const MOUNT_SELECTOR = '[data-mobile-nav-mount]';

  let mountNode = null;
  let activePanel = 'root';

  /* ---------------- Utilities ---------------- */

  function getTemplate() {
    return document.getElementById(TEMPLATE_ID);
  }

  function getDrawerDetails() {
    return document.getElementById('Details-menu-drawer-container');
  }

  function getHeaderDrawerInstance() {
    const details = getDrawerDetails();
    if (!details) return null;
    return details.closest('header-drawer');
  }

  function requestDrawerClose() {
    const drawer = getHeaderDrawerInstance();
    if (!drawer) return;
    if (typeof drawer.close === 'function') drawer.close();
  }

  function resolveMountNode() {
    // Mount point lives inside the drawer shell (Liquid authority).
    // Find it fresh, because Shopify may re-render header.
    const details = getDrawerDetails();
    if (!details) return null;
    const node = details.querySelector(MOUNT_SELECTOR);
    return node || null;
  }

  function clearMount() {
    if (!mountNode) return;
    mountNode.innerHTML = '';
    activePanel = 'root';
  }

  function mountContent() {
    const template = getTemplate();
    if (!template) return;

    mountNode = resolveMountNode();
    if (!mountNode) return;

    const clone = template.content.cloneNode(true);
    mountNode.innerHTML = '';
    mountNode.appendChild(clone);

    bindInternalControls();
    showPanel('root');
  }

  /* ---------------- State ---------------- */

  function showPanel(panelId) {
    if (!mountNode) return;

    const panels = mountNode.querySelectorAll('[data-panel]');
    panels.forEach((panel) => {
      const isActive = panel.getAttribute('data-panel') === panelId;
      panel.setAttribute('aria-hidden', String(!isActive));
    });

    activePanel = panelId;
  }

  /* ---------------- Events ---------------- */

  function bindInternalControls() {
    if (!mountNode) return;

    // Forward (root → category)
    mountNode.querySelectorAll('[data-forward]').forEach((btn) => {
      btn.addEventListener('click', () => {
        const target = btn.getAttribute('data-forward');
        if (target) showPanel(target);
      });
    });

    // Back (category → root)
    mountNode.querySelectorAll('[data-back]').forEach((btn) => {
      btn.addEventListener('click', () => {
        showPanel('root');
      });
    });

    // Internal link navigation → close drawer
    mountNode.querySelectorAll('[data-panel] a[href]').forEach((link) => {
      link.addEventListener('click', () => {
        requestDrawerClose();
      });
    });
  }

  /* ---------------- Portal Lifecycle ---------------- */

  function openNav() {
    mountContent();
  }

  // React ONLY to Shopify drawer state changes
  (function bindToDrawerToggle() {
    const details = getDrawerDetails();
    if (!details) return;

    details.addEventListener('toggle', () => {
      if (!details.open) {
        clearMount();
      }
    });
  })();

  /* ---------------- Global Bindings ---------------- */

  // Hamburger trigger(s) — mount content when drawer is opened by Shopify
  document.addEventListener('click', (e) => {
    const trigger = e.target.closest(`[${OPEN_ATTR}]`);
    if (!trigger) return;

    // Let <summary> do its native toggle; we only prepare content.
    // Preventing default here can break the details toggle in some DOM arrangements.
    // So: do NOT preventDefault.
    openNav();
  });

  // Root X button inside mounted nav (delegated, mount-safe)
  document.addEventListener(
    'click',
    (e) => {
      const btn =
        e.target && e.target.closest ? e.target.closest('[data-mobile-menu-close]') : null;

      if (!btn) return;

      const node = resolveMountNode();
      if (!node || !node.contains(btn)) return;

      e.preventDefault();
      e.stopPropagation();

      requestDrawerClose();
    },
    true
  );

  // MMO CTA hit-testing (mount-safe, no DOM hotspots)
  document.addEventListener(
    'pointerup',
    (e) => {
      const node = resolveMountNode();
      if (!node) return;

      const host = e.target && e.target.closest ? e.target.closest('[data-mmo-cta]') : null;
      if (!host) return;
      if (!node.contains(host)) return;

      if (e.button != null && e.button !== 0) return;

      const rect = host.getBoundingClientRect();
      if (!rect.width || !rect.height) return;

      const xPct = ((e.clientX - rect.left) / rect.width) * 100;
      const yPct = ((e.clientY - rect.top) / rect.height) * 100;

      const cs = getComputedStyle(host);
      const start = {
        x: parseFloat(cs.getPropertyValue('--mmo-start-x')) || 0,
        y: parseFloat(cs.getPropertyValue('--mmo-start-y')) || 0,
        w: parseFloat(cs.getPropertyValue('--mmo-start-w')) || 0,
        h: parseFloat(cs.getPropertyValue('--mmo-start-h')) || 0,
      };

      const upload = {
        x: parseFloat(cs.getPropertyValue('--mmo-upload-x')) || 0,
        y: parseFloat(cs.getPropertyValue('--mmo-upload-y')) || 0,
        w: parseFloat(cs.getPropertyValue('--mmo-upload-w')) || 0,
        h: parseFloat(cs.getPropertyValue('--mmo-upload-h')) || 0,
      };

      const inZone = (x, y, z) =>
        x >= z.x && x <= z.x + z.w && y >= z.y && y <= z.y + z.h;

      const hitStart = inZone(xPct, yPct, start);
      const hitUpload = inZone(xPct, yPct, upload);

      if (!hitStart && !hitUpload) return;

      e.preventDefault();
      e.stopPropagation();
      if (e.stopImmediatePropagation) e.stopImmediatePropagation();

      if (hitUpload) {
        const uploadUrl = '/pages/match-my-outfit?mode=upload';
        requestDrawerClose();
        window.location.assign(uploadUrl);
        return;
      }

      const startUrl =
        host.getAttribute('data-mmo-start-url') || '/pages/match-my-outfit?mode=start';
      requestDrawerClose();
      window.location.assign(startUrl);
    },
    true
  );

  // Route change / hard navigation safety
  window.addEventListener('pageshow', () => {
    clearMount();
  });
})();
